import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { taskPaths } from '../../tasks/queries/paths';
import { readProgress } from '../utils/progress';
import { tierKeys } from '../queries/keys';
import { tierPaths } from '../queries/paths';

// TSS vite/03 — server state for the `tiers` entity.

export const TIERS_PAGE_SIZE = 25;   // the backend default; hard cap is 100

// Takes project_id, phase and tier_state on top of limit/offset.
// Empty filters are dropped: the backend turns a parameter into an equality,
// so `phase=''` would ask for the tiers whose phase is the empty string.
export function useTiersList({ limit = TIERS_PAGE_SIZE, offset = 0, projectId, tierState } = {}) {
    const params = { limit, offset };
    if (projectId) params.project_id = projectId;
    if (tierState) params.tier_state = tierState;

    return useQuery({
        queryKey: tierKeys.list(params),
        queryFn: ({ signal }) => api.get(tierPaths.list(), { params, signal }).then(r => r.data),
        placeholderData: keepPreviousData,
    });
}

// HOW FAR EACH TIER GOT, for a page of them.
//
// Core publishes no progress of its own, so it is three counts per tier: all
// its tasks, the done ones and the discarded ones — plus the proposals, said
// apart. Each asks for ONE row (`limit: 1`) and reads `pagination.total`, which
// every listing carries: the number is the answer and the rows are not.
//
// Four small requests per row is the price of a count Core does not keep. They
// are cached for a minute and live under the `tasks` key, so authorizing or
// accepting a task refreshes them.
const COUNTED = [null, 'done', 'discarded', 'proposed'];
const PROGRESS_STALE_MS = 60_000;

export function useTierProgress(tierIds = []) {
    const queries = useQueries({
        queries: tierIds.flatMap(tierId => COUNTED.map(state => ({
            queryKey: tierKeys.progress(tierId, state),
            queryFn: ({ signal }) => {
                const params = { limit: 1, offset: 0, tier_id: tierId };
                if (state) params.workflow_state = state;
                return api
                    .get(taskPaths.list(), { params, signal })
                    .then(r => r.data?.pagination?.total ?? 0);
            },
            staleTime: PROGRESS_STALE_MS,
        }))),
    });

    return useMemo(() => {
        const byTier = new Map();
        tierIds.forEach((tierId, index) => {
            const base = index * COUNTED.length;
            const [total, done, discarded, proposed] = COUNTED.map((_, i) => queries[base + i]?.data);
            byTier.set(String(tierId), readProgress({ total, done, discarded, proposed }));
        });
        return byTier;
    }, [tierIds, queries]);
}

export function useTier(id) {
    return useQuery({
        queryKey: tierKeys.detail(id),
        queryFn: ({ signal }) => api.get(tierPaths.detail(id), { signal }).then(r => r.data),
        enabled: !!id,
    });
}

export function useCreateTier() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(tierPaths.list(), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: tierKeys.all }),
    });
}

export function useUpdateTier(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(tierPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: tierKeys.detail(id) });
            qc.invalidateQueries({ queryKey: tierKeys.all });
        },
    });
}

// The picker's fetcher and resolver. They live here because the endpoint does.
//
// THIS LIST HAS NO `q`. Since tier 12 only clients, users, teams and projects
// take a search term; `GET /admin/tiers` filters by project_id, phase and
// tier_state and nothing else. So a picker over tiers cannot search by text,
// and pretending otherwise would draw a box that ignores what is typed.
//
// What the contract DOES give is the filter that actually matters here: a task
// belongs to a tier of its project, so narrowing by project is exact rather
// than a guess, and it cuts the candidates to the handful that can be right.
export function tierOptions(projectId) {
    return (_q, signal) => {
        const params = { limit: 20, offset: 0 };
        if (projectId) params.project_id = projectId;
        return api
            .get(tierPaths.list(), { params, signal })
            .then(r => r.data.items ?? []);
    };
}

// Resolves the tier a saved row points at when it is not in the page above.
// Without this the field reads `#57`, which looks like missing data.
export function resolveTier(id, signal) {
    return api
        .get(tierPaths.detail(id), { signal })
        .then(r => r.data?.tier ?? r.data)
        .catch(() => null);
}
